import React, { useState, useEffect } from 'react';
import { Languages, Download, AlertCircle, FileText, CheckCircle2 } from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import FileUpload from '../components/common/FileUpload';
import { translationApi, Language, TranslationTask } from '../services/translationApi';
import { getErrorMessage } from '../utils/errorHandler';
import { getUrlWithToken } from '../utils/urlUtils';
import { useTranslationProgress } from '../hooks/useTranslationProgress';
import ProgressBar from '../components/common/ProgressBar';
import TranslationHistory from '../components/translation/TranslationHistory';
import './TranslateDoc.css';

const TranslateDoc: React.FC = () => {
    const [file, setFile] = useState<File | null>(null);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [languagesError, setLanguagesError] = useState<string>('');
    const [targetLang, setTargetLang] = useState<string>('en');

    const [task, setTask] = useState<TranslationTask | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string>('');
    const [refreshHistory, setRefreshHistory] = useState(0);

    // Fetch supported languages on mount
    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const langs = await translationApi.getLanguages();
                setLanguages(langs);
            } catch (err) {
                console.error('Failed to load languages', err);
                setLanguagesError('获取语言列表失败');
            }
        };
        fetchLanguages();
    }, []);

    // 使用 SSE Hook 监听状态
    const progress = useTranslationProgress(task?.task_id || null);

    // 同步 SSE 的完成/失败状态到 task (主要为了更新下载链接等)
    useEffect(() => {
        if (!task) return;
        if (progress.status === 'completed' && task.status !== 'completed') {
            setTask(prev => prev ? { ...prev, status: 'completed', download_url: progress.downloadUrl } : prev);
            setRefreshHistory(r => r + 1); // Refresh history on completion
        } else if (progress.status === 'failed' && task.status !== 'failed') {
            setTask(prev => prev ? { ...prev, status: 'failed', error_message: progress.error } : prev);
            setRefreshHistory(r => r + 1); // Refresh history on failure
        }
    }, [progress.status, progress.downloadUrl, progress.error, task?.task_id]);

    const handleFilesSelected = (files: File[]) => {
        if (files.length > 0) {
            setFile(files[0]); // Translation MVP works with 1 file at a time
            setError('');
            setTask(null); // Reset task state if new file is chosen
        }
    };

    const handleTranslate = async () => {
        if (!file) {
            setError('请选择文件');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const initialTask = await translationApi.translateQuick(file, targetLang);
            setTask(initialTask);
        } catch (err: any) {
            const message = getErrorMessage(err, 'convert');
            setError(message || '创建翻译任务失败');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDownload = () => {
        if (!task || !task.download_url) return;

        const token = localStorage.getItem('auth_token');
        const finalUrl = getUrlWithToken(task.download_url, token);
        const a = document.createElement('a');
        a.href = finalUrl;
        a.download = '';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const resetState = () => {
        setFile(null);
        setTask(null);
        setError('');
    };

    const isProcessing = task?.status === 'pending' || task?.status === 'translating';

    return (
        <div className="translate-page">
            <div className="container">
                <header className="page-header">
                    <h1 className="page-title">
                        <Languages className="title-icon" size={32} />
                        文档内容翻译
                    </h1>
                    <p className="page-subtitle">
                        一键翻译您的 PDF、Word、Excel 文档，完美保留原始排版格式
                    </p>
                </header>

                <main className="page-content">
                    <Card glass>
                        <div className="translate-container">
                            {!task && !isProcessing && (
                                <>
                                    <div className="upload-section">
                                        <FileUpload
                                            onFilesSelected={handleFilesSelected}
                                            accept=".pdf,.docx,.xlsx"
                                            multiple={false}
                                            maxSize={50}
                                        />
                                    </div>

                                    <div className="options-section">
                                        <div className="form-group">
                                            <label htmlFor="target-lang">想要翻译成什么语言？</label>
                                            <select
                                                id="target-lang"
                                                value={targetLang}
                                                onChange={(e) => setTargetLang(e.target.value)}
                                                className="language-select"
                                                disabled={isSubmitting}
                                            >
                                                {languages.map(lang => (
                                                    <option key={lang.code} value={lang.code}>
                                                        {lang.name}
                                                    </option>
                                                ))}
                                                {languages.length === 0 && !languagesError && (
                                                    <option value="en" disabled>English (加载中..)</option>
                                                )}
                                                {languagesError && (
                                                    <option value="en" disabled>{languagesError}</option>
                                                )}
                                            </select>
                                        </div>
                                    </div>

                                    {error && (
                                        <div className="error-message">
                                            <AlertCircle size={20} />
                                            <span>{error}</span>
                                        </div>
                                    )}

                                    <div className="action-section">
                                        <Button
                                            size="lg"
                                            onClick={handleTranslate}
                                            disabled={!file || isSubmitting}
                                            isLoading={isSubmitting}
                                        >
                                            <Languages size={20} />
                                            开始翻译
                                        </Button>
                                    </div>
                                </>
                            )}

                            {task && (
                                <div className="status-section">
                                    <div className="task-card">
                                        <div className="task-info">
                                            <FileText size={32} className="task-icon" />
                                            <div className="task-details">
                                                <h4>{task.source_filename}</h4>
                                                <p>目标语言: <strong>{languages.find(l => l.code === task.target_language)?.name || task.target_language}</strong></p>
                                            </div>
                                        </div>

                                        <div className="task-status">
                                            {isProcessing && (
                                                <ProgressBar status={progress.status as any} stage={progress.stage} />
                                            )}

                                            {task.status === 'completed' && (
                                                <div className="success-state">
                                                    <CheckCircle2 size={24} className="success-icon" />
                                                    <span>已完成</span>
                                                </div>
                                            )}

                                            {task.status === 'failed' && (
                                                <div className="failed-state">
                                                    <AlertCircle size={24} className="error-icon" />
                                                    <span>失败: {task.error_message || '未知错误'}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="task-actions">
                                            {task.status === 'completed' && (
                                                <Button size="lg" onClick={handleDownload} variant="primary">
                                                    <Download size={20} />
                                                    下载文件
                                                </Button>
                                            )}

                                            <Button
                                                variant="outline"
                                                onClick={resetState}
                                            >
                                                {isProcessing ? '取消当前等待' : '翻译其他文件'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>

                    <TranslationHistory onRefreshTrigger={refreshHistory} />
                </main>
            </div>
        </div>
    );
};

export default TranslateDoc;
